// AgriTycoon.jsx
import React, { useEffect, useState, useRef } from 'react';

// ------- CONFIG -------
const INITIAL_MONEY = 500;
const INITIAL_PLOTS = 6; 
const MAX_PLOTS = 18;
const DAY_TICK_MS = 6000; 

const CROPS = {
  Wheat: { seedCost: 10, growthDays: 2, baseYield: 5, icon: '🌾' },
  Corn: { seedCost: 18, growthDays: 3, baseYield: 9, icon: '🌽' },
  Tomato: { seedCost: 12, growthDays: 2, baseYield: 6, icon: '🍅' },
  Mango: { seedCost: 30, growthDays: 5, baseYield: 25, icon: '🥭' }
};

function randRange(min, max) { return Math.floor(Math.random()*(max-min+1))+min; }

function createInitialState(){
  return {
    money: INITIAL_MONEY,
    plots: Array(INITIAL_PLOTS).fill(null),
    day: 1,
    market: Object.fromEntries(Object.keys(CROPS).map(name => [name, randRange(8,22)])),
    ownedSeeds: Object.fromEntries(Object.keys(CROPS).map(name => [name, 2])),
    upgrades: { extraPlots:0, fertilizer:0 },
    missions: generateMissions(1),
    stats: { totalEarned:0, totalHarvested:0 }
  };
}

function generateMissions(day){
  return [
    { id: `earn-${day}`, type:'earn', target: 100 + (day*10), progress:0, reward:50 },
    { id: `harvest-${day}`, type:'harvest', target: 3 + Math.floor(day/3), progress:0, reward:30 }
  ];
}

export default function AgriTycoon(){
  const [state, setState] = useState(() => {
    try{
      const saved = localStorage.getItem('agri_tycoon_v1');
      return saved ? JSON.parse(saved) : createInitialState();
    } catch(e){ return createInitialState(); }
  });

  const [selectedCrop, setSelectedCrop] = useState('Wheat');
  const [autoTick, setAutoTick] = useState(true);
  const tickRef = useRef(null);
  const [message, setMessage] = useState('Welcome to Agri Tycoon!');
  const [showHelp, setShowHelp] = useState(false);


  useEffect(()=>{ localStorage.setItem('agri_tycoon_v1', JSON.stringify(state)); },[state]);

  useEffect(()=>{
    if(autoTick){
      tickRef.current = setInterval(()=>advanceDay(), DAY_TICK_MS);
    } else if(tickRef.current){
      clearInterval(tickRef.current); tickRef.current = null;
    }
    return ()=>{ if(tickRef.current){ clearInterval(tickRef.current); tickRef.current=null; } };
  },[autoTick, state]);

  function up(fn){ setState(s => { const ns = fn(JSON.parse(JSON.stringify(s))); return ns; }); }

  function buySeed(cropName, qty=1){
    const cost = CROPS[cropName].seedCost * qty;
    if(state.money < cost){ setMessage('Not enough money to buy seeds.'); return; }
    up(s=>{ s.money -= cost; s.ownedSeeds[cropName] = (s.ownedSeeds[cropName]||0)+qty; return s; });
    setMessage(`Bought ${qty} ${cropName} seed(s)`);
  }

  function plantOnPlot(idx){
    if(idx<0 || idx>=state.plots.length) return;
    const cropName = selectedCrop;
    if(!state.ownedSeeds[cropName] || state.ownedSeeds[cropName] <=0){ setMessage('Buy seeds first.'); return; }
    if(state.plots[idx]){ setMessage('Plot already occupied.'); return; }
    up(s=>{
      s.ownedSeeds[cropName] = Math.max(0, s.ownedSeeds[cropName]-1);
      s.plots[idx] = { crop: cropName, plantedDay: s.day, growthRemaining: CROPS[cropName].growthDays - 1, qtyModifier:1 };
      return s;
    });
    setMessage(`Planted ${cropName} on plot ${idx+1}`);
  }

  function harvestPlot(idx){
    const p = state.plots[idx];
    if(!p){ setMessage('Nothing to harvest here.'); return; }
    if(p.growthRemaining > 0){ setMessage('Crop not ready yet.'); return; }
    const cropDef = CROPS[p.crop];
    const price = state.market[p.crop] || 10;
    const randomness = (randRange(80,120))/100;
    const qty = Math.max(1, Math.floor(cropDef.baseYield * p.qtyModifier * randomness));
    const gain = qty * price;
    up(s=>{
      s.money += gain; s.plots[idx] = null;
      s.stats.totalEarned += gain; s.stats.totalHarvested += qty;
      s.missions.forEach(m=>{
        if(m.type==='earn'){ m.progress = Math.min(m.target, m.progress + gain); }
        if(m.type==='harvest'){ m.progress = Math.min(m.target, m.progress + 1); }
      });
      return s;
    });
    setMessage(`Harvested ${qty} ${p.crop} for ₹${gain}`);
  }

  function advanceDay(){
    up(s=>{
      s.day += 1;
      for(let i=0;i<s.plots.length;i++){ if(s.plots[i]) s.plots[i].growthRemaining = Math.max(0, s.plots[i].growthRemaining-1); }
      Object.keys(s.market).forEach(c => { s.market[c] = Math.max(3, s.market[c] + randRange(-3,4)); });

      const evRoll = Math.random();
      if(evRoll < 0.12){ const affected = Object.keys(CROPS)[randRange(0,Object.keys(CROPS).length-1)];
        s.plots.forEach(p => { if(p && p.crop===affected) p.qtyModifier *= 0.6; });
        s._lastEvent = `Pest outbreak affected ${affected} - yields reduced.`; 
      } else if(evRoll < 0.22){ const blessed = Object.keys(CROPS)[randRange(0,Object.keys(CROPS).length-1)];
        s.plots.forEach(p => { if(p && p.crop===blessed) p.qtyModifier *= 1.3; });
        s._lastEvent = `Good weather boosted ${blessed} yields!`; 
      } else { s._lastEvent = null; }

      s.missions.forEach(m=>{
        if(!m._done && m.progress >= m.target){ s.money += m.reward; m._done=true; m._claimed=true; s._missionMsg=`Mission '${m.id}' done: +₹${m.reward}`; }
      });
      if(s.money > 5000){ s.money += Math.floor(s.money*0.01); }
      return s;
    });
  }

  function expandPlots(){
    up(s=>{
      const cost = 200 + s.plots.length*50;
      if(s.money<cost){ setMessage('Not enough money to expand plots.'); return s; }
      if(s.plots.length >= MAX_PLOTS){ setMessage('Max plots reached.'); return s; }
      s.money -= cost; s.plots.push(null); return s;
    });
  }

  function buyUpgrade(name){
    up(s=>{
      if(name==='fertilizer'){
        const cost = 300*(s.upgrades.fertilizer+1);
        if(s.money < cost){ setMessage('Not enough for fertilizer upgrade.'); return s; }
        s.money -= cost; s.upgrades.fertilizer += 1; setMessage('Fertilizer purchased.'); 
      }
      return s;
    });
  }

  function resetGame(){ if(window.confirm('Reset game?')) { setState(createInitialState()); setMessage('New farm started!'); } }

  function plotStatus(p){ if(!p) return 'Empty'; if(p.growthRemaining>0) return `${p.crop} (growing: ${p.growthRemaining}d)`; return `${p.crop} (ready)`; }

  return (
    <div style={{fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial', padding:16, maxWidth:1200, margin:'0 auto'}}>
      <style>{`
        .topbar{display:flex; gap:8px; flex-wrap:wrap; align-items:center; justify-content:space-between}
        .panel{background:#fff; padding:12px; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.05)}
        .farm-grid{display:grid; grid-template-columns:repeat(auto-fit, minmax(100px,1fr)); gap:10px}
        .plot{background:linear-gradient(180deg,#fefefe,#f0fff0); padding:10px; border-radius:8px; text-align:center; cursor:pointer; min-height:100px; display:flex; flex-direction:column; justify-content:center; align-items:center}
        .plot.empty{opacity:0.9}
        .controls{display:grid; grid-template-columns:repeat(auto-fit,minmax(120px,1fr)); gap:8px}
        @media(max-width:800px){
          .topbar{flex-direction:column; align-items:flex-start}
        }
      `}</style>

      <div className="topbar">
        <div style={{display:'flex', gap:10, flexWrap:'wrap', alignItems:'center'}}>
          <h2 style={{margin:0}}>🌾 Agri Tycoon</h2>
          <div className="panel">Day: <strong>{state.day}</strong></div>
          <div className="panel">Money: <strong>₹{state.money}</strong></div>
          <div className="panel">Plots: <strong>{state.plots.length}</strong></div>
        </div>
        <div className="flex flex-row gap-2 justify-center mt-0">
          <button onClick={()=>setAutoTick(a=>!a)} className="bg-blue-700 text-white px-3 py-1 rounded">{autoTick? 'Pause' : 'Auto-Play'}</button>
          <button onClick={advanceDay} className="bg-blue-700 text-white px-3 py-1 rounded">Advance Day</button>
          <button onClick={resetGame} className="bg-blue-700 text-white px-3 py-1 rounded">Reset</button>
          <button 
            onClick={()=>setShowHelp(h=>!h)} 
            className="bg-teal-700 text-white px-3 py-1 rounded"
          >
            Help
          </button>
        </div>
      </div>

      <div style={{flex:'0 0 280px'}}>
          {showHelp && (
            <div className="panel mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded text-sm" style={{lineHeight:1.5}}>
              <h4 className="text-lg font-semibold mb-1">Agri Tycoon Help & Tips</h4>
              <ul className="list-disc pl-5">
                <li><strong>Planting Crops:</strong> Select a crop from the dropdown and click on an empty plot to plant. Each crop has a different growth time and yield.</li>
                <li><strong>Harvesting:</strong> Tap on a plot with a ready crop to harvest. Earnings depend on the crop type and current market price.</li>
                <li><strong>Market:</strong> Crop prices fluctuate daily. Keep an eye on market prices to maximize profits.</li>
                <li><strong>Buying Seeds:</strong> Use the shop to buy seeds. You need seeds in inventory to plant crops.</li>
                <li><strong>Upgrades:</strong> Fertilizer increases crop yield and growth speed. Expand plots to grow more crops simultaneously.</li>
                <li><strong>Advance Day:</strong> The game auto-advances each day, but you can manually advance for faster progress.</li>
                <li><strong>Random Events:</strong> Weather and pest events affect crop yields. Watch messages for important events.</li>
                <li><strong>Missions:</strong> Complete daily missions to earn extra money. Check progress in the Missions panel.</li>
                <li><strong>Tips:</strong> Harvest crops when ready, buy seeds in bulk, and plan plot expansions wisely for maximum profits.</li>
                <li><strong>Save & Persistence:</strong> Your farm progress is saved automatically in your browser's local storage.</li>
              </ul>
            </div>
          )}
          <div className='flex felx-row gap-2 mt-4'>
            <div className="w-1/2 bg-gray-100 rounded-lg px-4 py-2">
              <h4 style={{marginTop:0}}>Missions</h4>
              <ul>
                {state.missions.map(m=> (
                  <li key={m.id} style={{marginBottom:6}}>
                    <strong>{m.type}</strong> — {m.progress}/{m.target} {m._done? ' (done)':''}
                    {m._done ? <span> — claimed ₹{m.reward}</span> : null}
                  </li>
                ))}
              </ul>
            </div>

            <div className="w-1/2 bg-gray-100 rounded-lg px-4 py-2">
              <h4 style={{marginTop:0}}>Stats</h4>
              <div>Total Earned: ₹{state.stats.totalEarned}</div>
              <div>Total Harvested: {state.stats.totalHarvested}</div>
              <div>Fertilizer Level: {state.upgrades.fertilizer}</div>
            </div>
          </div>
        </div>

      <div style={{display:'flex', flexWrap:'wrap', gap:12, marginTop:12}}>
        <div style={{flex:'1 1 600px'}}>
          <div className="panel">
            <h3 className="text-blue-700" style={{marginTop:0}}>Your Farm</h3>
            <div className="farm-grid">
              {state.plots.map((p,idx)=> (
                <div key={idx} className={`plot ${p?'occupied':'empty'}`} onClick={() => p? harvestPlot(idx) : plantOnPlot(idx)}>
                  <div style={{fontSize:30}}>{p? CROPS[p.crop].icon : '🧑‍🌾'}</div>
                  <div style={{marginTop:6, fontWeight:600}}>{p? p.crop : 'Empty Plot'}</div>
                  <div style={{fontSize:12, color:'#444'}}>{plotStatus(p)}</div>
                  {p && p.growthRemaining===0 && <div style={{marginTop:4, fontSize:12, color:'#0a7'}}>Ready — tap to harvest</div>}
                </div>
              ))}
            </div>
          </div>

          <div style={{display:'flex', flexWrap:'wrap', gap:12, marginTop:12}}>
            <div className="panel" style={{flex:'1 1 300px'}}>
              <h4 style={{marginTop:0}}>Market</h4>
              <div style={{display:'grid', gap:6}}>
                {Object.keys(state.market).map(c => (
                  <div key={c} style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <div style={{display:'flex', gap:6, alignItems:'center'}}><span style={{fontSize:18}}>{CROPS[c].icon}</span><strong>{c}</strong></div>
                    <div>₹{state.market[c]}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="panel" style={{flex:'1 1 200px'}}>
              <h4 style={{marginTop:0}}>Shop & Controls</h4>
              <div className="controls">
                <select value={selectedCrop} onChange={e=>setSelectedCrop(e.target.value)} style={{padding:6}}>
                  {Object.keys(CROPS).map(k=> <option key={k} value={k}>{k}</option>)}
                </select>
                <button onClick={() => buySeed(selectedCrop,1)} className="bg-green-600 text-white rounded px-2 py-1">Buy seed (₹{CROPS[selectedCrop].seedCost})</button>
                <button onClick={expandPlots} className="bg-blue-600 text-white rounded px-2 py-1">Expand plot</button>
                <button onClick={() => buyUpgrade('fertilizer')} className="bg-yellow-600 text-white rounded px-2 py-1">Fertilizer</button>
              </div>
              <div style={{marginTop:10}}>
                <strong>Seeds:</strong>
                <ul style={{margin:4, paddingLeft:18}}>
                  {Object.keys(state.ownedSeeds).map(k=> <li key={k}>{k}: {state.ownedSeeds[k]} seeds</li>)}
                </ul>
              </div>
            </div>
          </div>

          <div className="panel" style={{marginTop:12}}>
            <h4 style={{marginTop:0}}>Events & Messages</h4>
            <div style={{minHeight:40}}>{state._lastEvent || message}</div>
          </div>
        </div>

        
      </div>

      <footer className="bg-gray-100 text-center mt-4 p-2 text-sm text-gray-600 rounded">Agri Tycoon — solo farm economy simulator. Save is automatic.</footer>
    </div>
  );
}
